import "./entryGate.css";
import { t } from "../i18n/index.js";
import { BALL_MENU_SVG } from "./ballMark.js";
import { mountGoogleButton } from "./googleSignIn.js";
import { loadNickname, storeNickname, type NicknameService } from "../online/nickname.js";


/**
 * Puerta de entrada: iniciar sesion o entrar de invitado.
 *
 * Las dos salidas llevan al mismo sitio. La de invitado es UN clic mas un
 * nombre, sin nada mas: es la que tiene que seguir siendo trivial, porque un
 * juego de navegador que pide cuenta antes de dejarte jugar pierde a la mayoria
 * ahi mismo.
 *
 * Solo aparece cuando no hay sesion todavia. A quien ya entro se le manda
 * derecho al menu: enseñar la puerta en cada arranque seria devolver la
 * friccion por la puerta de atras.
 */
export interface EntryGateHandle {
  /** Resuelve con el nombre elegido cuando el jugador ha entrado. */
  show(): Promise<string>;
  /** Client ID que manda el server. null = este server no tiene Google. */
  setGoogle(clientId: string | null): void;
  /** El server acepto la cuenta de Google. */
  googleAccepted(nickname: string): void;
  /** El server rechazo el token o el codigo. */
  showError(message: string): void;
  hide(): void;
  dispose(): void;
}

export interface EntryGateOptions {
  /** ID token de Google, para que el server lo verifique. */
  onGoogle(credential: string): void;
  /** Codigo de recuperacion tecleado a mano. */
  onCode(code: string): void;
  /**
   * Para reservar el apodo en el servidor (son unicos) y decir mientras se
   * escribe si esta libre. Sin el, se guarda solo en local.
   */
  nickname?: NicknameService;
}


export function createEntryGate(
  root: HTMLElement,
  options: EntryGateOptions,
): EntryGateHandle {
  const overlay = document.createElement("div");
  overlay.id = "entry-gate";
  overlay.className = "hub-overlay gate-overlay is-hidden";
  overlay.innerHTML = `
    <div class="menu-shell gate-shell">
      <header class="gate-brand">
        <div class="gate-ball">${BALL_MENU_SVG}</div>
        <h1 class="gate-title" aria-label="FYNTA">FYNTA</h1>
      </header>

      <div class="gate-step" data-step="choose">
        <div class="gate-block gate-block--google" data-google-block hidden>
          <div class="gate-google-btn" data-google-btn></div>
          <p class="gate-note">${t("Tu carrera te sigue a cualquier móvil u ordenador.")}</p>
        </div>

        <!--
          "Elige tu apodo", no "jugar como invitado": lo primero que se hace
          aqui es decir como te llamas, y eso es lo que veran los demas. La
          palabra "invitado" sonaba a jugador de segunda.
        -->
        <button type="button" class="gate-guest" data-guest>
          <strong>${t("ELIGE TU APODO")}</strong>
          <span>${t("Así te verán en las salas y en el chat. Es solo tuyo.")}</span>
        </button>

        <button type="button" class="gate-link" data-code-open>${t("¿Tienes un código?")}</button>

        <form class="gate-code" data-code-form hidden>
          <input
            type="text"
            class="gate-input gate-input--code"
            data-code-input
            placeholder="${t("pegar código")}"
            maxlength="20"
            autocomplete="off"
            spellcheck="false"
          />
          <button type="submit" class="gate-go">${t("Usar")}</button>
        </form>
      </div>

      <form class="gate-step" data-step="nick" hidden>
        <label class="gate-label" for="gate-nick">${t("¿Cómo te llamas?")}</label>
        <input
          type="text"
          class="gate-input"
          id="gate-nick"
          data-nick
          maxlength="16"
          autocomplete="nickname"
          autocapitalize="off"
          spellcheck="false"
          placeholder="${t("tu nombre")}"
          aria-describedby="gate-nick-status"
        />
        <p class="gate-nick-status" id="gate-nick-status" data-nick-status aria-live="polite"></p>
        <button type="submit" class="gate-go gate-go--big" data-nick-go>${t("EMPEZAR")}</button>
        <button type="button" class="gate-link" data-nick-back>${t("‹ Volver")}</button>
      </form>

      <output class="gate-error" data-error hidden></output>
      <nav class="gate-legal" aria-label="${t("Información legal")}">
        <a href="/privacy.html" target="_blank" rel="noopener">${t("Privacidad")}</a>
        <span aria-hidden="true">·</span>
        <a href="/terms.html" target="_blank" rel="noopener">${t("Términos")}</a>
      </nav>
    </div>
  `;
  root.appendChild(overlay);

  const q = <T extends HTMLElement>(sel: string): T => overlay.querySelector(sel) as T;
  const chooseStep = q<HTMLElement>('[data-step="choose"]');
  const nickStep = q<HTMLFormElement>('[data-step="nick"]');
  const googleBlock = q<HTMLElement>("[data-google-block]");
  const googleBtn = q<HTMLElement>("[data-google-btn]");
  const guestBtn = q<HTMLButtonElement>("[data-guest]");
  const nickInput = q<HTMLInputElement>("[data-nick]");
  const nickBack = q<HTMLButtonElement>("[data-nick-back]");
  const codeOpen = q<HTMLButtonElement>("[data-code-open]");
  const codeForm = q<HTMLFormElement>("[data-code-form]");
  const codeInput = q<HTMLInputElement>("[data-code-input]");
  const errorOut = q<HTMLElement>("[data-error]");

  let resolveEntry: ((nick: string) => void) | null = null;
  let googleMounted = false;

  const step = (which: "choose" | "nick"): void => {
    chooseStep.hidden = which !== "choose";
    nickStep.hidden = which !== "nick";
    errorOut.hidden = true;
    if (which === "nick") nickInput.focus();
  };

  const nickStatus = q<HTMLElement>("[data-nick-status]");
  const nickGo = q<HTMLButtonElement>("[data-nick-go]");
  const service = options.nickname ?? null;

  const finish = (nick: string): void => {
    const clean = nick.trim().slice(0, 16);
    if (clean) storeNickname(clean);
    const done = resolveEntry;
    resolveEntry = null;
    overlay.classList.add("is-hidden");
    done?.(clean || loadNickname());
  };

  const setNickStatus = (text: string, tone: "" | "ok" | "bad" = ""): void => {
    nickStatus.textContent = text;
    nickStatus.dataset.tone = tone;
  };

  /*
   * Mientras escribes se pregunta si esta libre: los apodos son unicos, y
   * enterarse DESPUES de pulsar "EMPEZAR" es la peor forma de enterarse.
   */
  let checkTimer = 0;
  let checkSeq = 0;
  nickInput.addEventListener("input", () => {
    window.clearTimeout(checkTimer);
    const value = nickInput.value.trim();
    if (!service || !value) {
      setNickStatus("");
      return;
    }
    const seq = ++checkSeq;
    checkTimer = window.setTimeout(() => {
      void service.check(value).then((status) => {
        if (seq !== checkSeq) return;
        if (status === "free" || status === "yours") setNickStatus(t("✓ Libre"), "ok");
        else if (status === "taken") setNickStatus(t("✗ Ya lo tiene otro jugador"), "bad");
        else if (status === "invalid") setNickStatus(t("✗ Ese no vale. Prueba con otro."), "bad");
        else setNickStatus("");
      });
    }, 280);
  });

  guestBtn.addEventListener("click", () => step("nick"));
  nickBack.addEventListener("click", () => step("choose"));

  nickStep.addEventListener("submit", (event) => {
    event.preventDefault();
    const value = nickInput.value.trim();
    if (!value) {
      setNickStatus(t("Escribe cómo quieres que te llamen."), "bad");
      nickInput.focus();
      return;
    }
    if (!service) {
      finish(value);
      return;
    }
    nickGo.disabled = true;
    setNickStatus(t("Guardando…"));
    void service.set(value).then((result) => {
      nickGo.disabled = false;
      if (result.ok) {
        finish(result.nickname);
        return;
      }
      if (result.reason === "taken") setNickStatus(t("✗ Ya lo tiene otro jugador. Prueba con otro (un número también vale)."), "bad");
      else if (result.reason === "invalid") setNickStatus(t("✗ Ese no vale. Prueba con otro."), "bad");
      else setNickStatus(t("El servidor no contesta. Inténtalo de nuevo."), "bad");
      nickInput.focus();
    });
  });

  codeOpen.addEventListener("click", () => {
    codeForm.hidden = !codeForm.hidden;
    if (!codeForm.hidden) codeInput.focus();
  });

  codeForm.addEventListener("submit", (event) => {
    event.preventDefault();
    const code = codeInput.value.trim();
    if (code) options.onCode(code);
  });

  return {
    show(): Promise<string> {
      // Un "Jugador1234" que puso el servidor no es un nombre elegido: vacio.
      const current = loadNickname();
      nickInput.value = /^Jugador\d+$/.test(current) ? "" : current;
      setNickStatus("");
      step("choose");
      overlay.classList.remove("is-hidden");
      return new Promise<string>((resolve) => {
        resolveEntry = resolve;
      });
    },

    setGoogle(clientId): void {
      if (!clientId || googleMounted) return;
      googleMounted = true;
      googleBlock.hidden = false;
      void mountGoogleButton({
        clientId,
        parent: googleBtn,
        onCredential: options.onGoogle,
        // Si Google no carga, se esconde el bloque entero. Nunca un boton
        // muerto en la primera pantalla que ve alguien.
        onUnavailable: () => {
          googleBlock.hidden = true;
        },
      });
    },

    googleAccepted(nickname): void {
      finish(nickname);
    },

    showError(message): void {
      errorOut.hidden = false;
      errorOut.textContent = message;
    },

    hide(): void {
      overlay.classList.add("is-hidden");
    },

    dispose(): void {
      overlay.remove();
    },
  };
}
